import './BigQuestions.css';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import vorData from './vorData.json';

function Sparkline({ thesis }) {
  const PERSP  = ['participants', 'experts', 'future_leaders', 'public'];
  const PHASES = ['before', 'during', 'after'];
  const pts = PHASES.map(ph => {
    const vals = PERSP.map(p => thesis.results?.[p]?.[ph]?.agree ?? 0);
    return vals.reduce((a, b) => a + b, 0) / vals.length;
  });
  const W = 44, H = 16, pad = 2;
  const lo = Math.min(...pts), hi = Math.max(...pts);
  const span = hi - lo || 10;
  const coord = pts.map((v, i) => {
    const x = (i / (pts.length - 1)) * (W - pad * 2) + pad;
    const y = H - pad - ((v - lo) / span) * (H - pad * 2);
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');
  return (
    <svg width={W} height={H} viewBox={`0 0 ${W} ${H}`} className="bq-sparkline" aria-hidden="true">
      <polyline points={coord} fill="none" stroke="rgba(134,203,241,0.30)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function computeShift(thesis) {
  const perspectives = ['participants', 'experts', 'future_leaders', 'public'];
  let total = 0, count = 0;
  for (const p of perspectives) {
    const r = thesis.results?.[p];
    if (r?.before?.agree !== undefined && r?.after?.agree !== undefined) {
      total += r.after.agree - r.before.agree;
      count++;
    }
  }
  return count > 0 ? Math.round(total / count) : 0;
}

export default function BigQuestions({ highlightTopicId, highlightThesisId }) {
  const { t, i18n } = useTranslation();
  const lang    = i18n.language?.slice(0, 2) || 'en';
  const history = useHistory();

  const [thesisVotes, setThesisVotes] = useState({});
  const [topicIdx, setTopicIdx]       = useState(0);
  const [flashThesisId, setFlashThesisId] = useState(null);
  const touchStartX = useRef(null);

  const topics = vorData.topics;
  const AGREE_SIDE = ['agree', 'strongly_agree'];

  const handleVote = (thesisId, choice) => {
    setThesisVotes(prev => ({ ...prev, [thesisId]: choice }));
  };

  const getTitle = (obj) => {
    if (lang === 'de') return obj.title_de;
    if (lang === 'fr') return obj.title_fr;
    return obj.title_en;
  };

  const getText = (obj) => {
    if (lang === 'de') return obj.text_de;
    if (lang === 'fr') return obj.text_fr;
    return obj.text_en;
  };

  const getShortTitle = (obj) => {
    if (lang === 'de') return obj.shortTitle_de || obj.text_de;
    if (lang === 'fr') return obj.shortTitle_fr || obj.text_fr;
    return obj.shortTitle_en || obj.text_en;
  };

  const navigate = (newIdx) => {
    if (newIdx < 0 || newIdx >= topics.length) return;
    setTopicIdx(newIdx);
  };

  // Navigate to highlighted topic (from relatedTopicClick)
  useEffect(() => {
    if (!highlightTopicId || highlightThesisId) return;
    const idx = topics.findIndex(tp => tp.id === highlightTopicId);
    if (idx !== -1) setTopicIdx(idx);
  }, [highlightTopicId]);

  // Jump to thesis topic and flash it
  useEffect(() => {
    if (!highlightThesisId) return;
    const thesis = vorData.theses.find(th => th.id === highlightThesisId);
    if (thesis) {
      const idx = topics.findIndex(tp => tp.id === thesis.topicId);
      if (idx !== -1) setTopicIdx(idx);
    }
    const t1 = setTimeout(() => {
      setFlashThesisId(highlightThesisId);
      const t2 = setTimeout(() => setFlashThesisId(null), 1800);
      return () => clearTimeout(t2);
    }, 400);
    return () => clearTimeout(t1);
  }, [highlightThesisId]);

  // Swipe gesture
  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (dx < -40) navigate(topicIdx + 1);
    else if (dx > 40) navigate(topicIdx - 1);
    touchStartX.current = null;
  };

  const trackStyle = {
    transform: `translateX(${-topicIdx * 100}%)`,
    transition: 'transform 520ms cubic-bezier(0.25, 1.04, 0.5, 1)',
  };

  return (
    <div className="bq-scroll-area">
      <div className="bq-inner">

        {/* ── Paged topic navigator ── */}
        <div
          className="bq-pager"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {/* Header: arrows + sliding topic name + counter */}
          <div className="bq-pager-header">
            <button
              className="bq-pager-arrow"
              disabled={topicIdx === 0}
              onClick={() => navigate(topicIdx - 1)}
              aria-label="Previous topic"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 3L5 8l5 5"/>
              </svg>
            </button>

            <div className="bq-pager-center">
              <div className="bq-pager-track" style={trackStyle}>
                {topics.map((topic, i) => (
                  <div key={topic.id} className="bq-pager-title-slide">
                    <span className="bq-topic-name">{getTitle(topic)}</span>
                    <span className="bq-pager-counter">{i + 1} of {topics.length}</span>
                  </div>
                ))}
              </div>
            </div>

            <button
              className="bq-pager-arrow"
              disabled={topicIdx === topics.length - 1}
              onClick={() => navigate(topicIdx + 1)}
              aria-label="Next topic"
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none"
                stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 3l5 5-5 5"/>
              </svg>
            </button>
          </div>

          {/* Content pages */}
          <div className="bq-pager-content">
            <div className="bq-pager-track" style={trackStyle}>
              {topics.map((topic) => {
                const theses = vorData.theses.filter(th => th.topicId === topic.id);
                return (
                  <div key={topic.id} className="bq-pager-page">
                    <div className="bq-theses">
                      {theses.map((thesis, idx) => {

                        const vote  = thesisVotes[thesis.id];
                        const shift = computeShift(thesis);
                        const voted = !!vote;
                        return (
                          <div
                            key={thesis.id}
                            className={`bq-thesis-item${idx > 0 ? ' bq-thesis-item--divider' : ''}${voted ? ' bq-thesis-item--voted' : ''}${flashThesisId === thesis.id ? ' bq-thesis-item--flash' : ''}`}
                          >
                            <div className="bq-thesis-body">
                              <div className="bq-thesis-arc">
                                <svg width="38" height="38" viewBox="0 0 40 40" className="bq-thesis-arc-svg">
                                  {/* Arc: gap from 12 o'clock → 95° clockwise (just past 3 o'clock) */}
                                  <path d="M 32.95 21.13 A 13 13 0 1 1 20 7" stroke="#3B7CB8" strokeWidth="3.2" fill="none" strokeLinecap="round"/>
                                  <text
                                    x="20" y="20"
                                    textAnchor="middle"
                                    dominantBaseline="central"
                                    fill="#3B7CB8"
                                    fontSize="15"
                                    fontWeight="600"
                                    fontFamily="'DM Sans', sans-serif"
                                  >{idx + 1}</text>
                                </svg>
                              </div>
                              <div className="bq-thesis-content">
                                <p className="bq-thesis-short">{getShortTitle(thesis)}</p>
                                <p className="bq-thesis-statement">{getText(thesis)}</p>
                              </div>
                            </div>

                            {!voted && (
                              <div className="bq-vote-grid">
                                <button className="bq-vote-btn bq-vote-btn--neg bq-vote-btn--strong"
                                  onClick={() => handleVote(thesis.id, 'strongly_disagree')}>
                                  {t('bq.strongly_disagree')}
                                </button>
                                <button className="bq-vote-btn bq-vote-btn--pos bq-vote-btn--strong"
                                  onClick={() => handleVote(thesis.id, 'strongly_agree')}>
                                  {t('bq.strongly_agree')}
                                </button>
                                <button className="bq-vote-btn bq-vote-btn--neg"
                                  onClick={() => handleVote(thesis.id, 'disagree')}>
                                  {t('bq.disagree')}
                                </button>
                                <button className="bq-vote-btn bq-vote-btn--pos"
                                  onClick={() => handleVote(thesis.id, 'agree')}>
                                  {t('bq.agree')}
                                </button>
                              </div>
                            )}

                            {voted && (
                              <div className="bq-voted-reveal">
                                <div className="bq-voted-position">
                                  <span className="bq-voted-label">{t('bq.yourPosition')}</span>
                                  <span className={`bq-voted-choice bq-voted-choice--${AGREE_SIDE.includes(vote) ? 'pos' : 'neg'}`}>
                                    {t(`bq.${vote}`)}
                                  </span>
                                </div>
                                <div className="bq-sparkline-row">
                                  <Sparkline thesis={thesis} />
                                  {shift !== 0 && (
                                    <>
                                      <span className={`bq-shift-chip bq-shift-chip--${shift > 0 ? 'up' : 'down'}`}>
                                        {shift > 0 ? '▲' : '▼'}&thinsp;{shift > 0 ? '+' : ''}{shift}%
                                      </span>
                                      <span className="bq-shift-since">{t('bq.shiftSince')}</span>
                                    </>
                                  )}
                                  <button
                                    className="bq-view-details bq-view-details--right"
                                    onClick={() => history.push(`/thesis/${thesis.id}`)}
                                  >
                                    {t('bq.viewBarometer')}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <button className="bq-graben-entry" onClick={() => history.push('/standing')}>
                      <span className="bq-graben-title">Where Munich Voices currently stand</span>
                      <div className="bq-graben-miniaxis">
                        <div className="bq-graben-minitrack" />
                        <span className="bq-graben-minibridge" style={{ left: '20%', width: '54%' }} />
                        <span className="bq-graben-minidot" style={{ left: '74%', background: '#7ECBA5' }} />
                        <span className="bq-graben-minidot" style={{ left: '57%', background: '#86CBF1' }} />
                        <span className="bq-graben-minidot" style={{ left: '40%', background: '#D4A244' }} />
                        <span className="bq-graben-minidot" style={{ left: '20%', background: '#E88B7A' }} />
                      </div>
                      <svg className="bq-graben-arrow" width="13" height="13" viewBox="0 0 16 16" fill="none"
                        stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M6 3l5 5-5 5"/>
                      </svg>
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="takes-proto bq-proto">Prototype by Ferdinand Riedl · riedlsoftware.com</div>
      </div>
    </div>
  );
}
